/** Single direct-message row that the sidebar renders. */
export interface DmRow {
  channelId: string;
  displayName: string;
  avatarUrl?: string | null;
  isOnline: boolean;
  lastActiveAt?: number | null;
  userId?: string;
  lastMessageAt: number;
  createdAt: number;
  isCoworker?: boolean;
  isGuest?: boolean;
}

/**
 * Build the {@link DmRow} for a self-DM ("Notes") channel — the caller is the
 * only member. Returns `null` when the channel isn't the caller's self-DM.
 *
 * "Notes" is the marker `getOrCreateDirectDM` stamps on self-DMs (issue #1450;
 * migrations 133/134 backfilled old ones); it disambiguates from a 1:1 DM
 * whose peer's membership row was tombstoned (also one visible member = the
 * caller, but it keeps its peer's name). Rendered as "{name} (You)" with no
 * `userId`, so it flows into the personal DM bucket and behaves like any other
 * DM row.
 */
export function buildSelfDmRow(opts: {
  channelId: string;
  channelName: string | null | undefined;
  memberUserIds: string[];
  currentUserId: string;
  me: { display_name: string; avatar_url?: string | null } | undefined;
  createdAt: number;
  lastMessageAt: number;
}): DmRow | null {
  const { channelName, memberUserIds, currentUserId, me } = opts;
  if (channelName !== "Notes" || !me) return null;
  if (!memberUserIds.includes(currentUserId)) return null;
  if (memberUserIds.some((id) => id !== currentUserId)) return null;
  return {
    channelId: opts.channelId,
    displayName: `${me.display_name} (You)`,
    avatarUrl: me.avatar_url ?? undefined,
    isOnline: false,
    lastActiveAt: null,
    userId: undefined,
    lastMessageAt: opts.lastMessageAt,
    createdAt: opts.createdAt,
    isCoworker: false,
    isGuest: false,
  };
}

/** Minimal user shape the row builder reads (a superset of the Zero user). */
export interface DmUser {
  display_name: string;
  avatar_url?: string | null;
  is_online?: boolean | null;
  last_active_at?: number | null;
  is_deactivated?: boolean | null;
  is_coworker?: boolean | null;
  is_guest?: boolean | null;
}

interface DmChannelLike {
  id: string;
  name?: string | null;
  created_at?: number | null;
  members?: ReadonlyArray<{ user_id: string }> | null;
}

/**
 * Derive the deduped, name-sorted `DmRow[]` for the 1:1 DM list from the raw
 * `type="dm"` channels. Pure (no Zero/React) so it's unit-testable in
 * isolation. Handles three cases per channel: the caller's self-DM ("Notes",
 * via {@link buildSelfDmRow}), deactivated-peer DMs (hidden), and normal 1:1s
 * (deduped by peer, keeping the most-recently-active channel).
 */
export function buildDirectDmRows(opts: {
  channels: ReadonlyArray<DmChannelLike>;
  currentUserId: string;
  getUser: (id: string) => DmUser | undefined;
  getLastMessageAt: (channelId: string) => number;
}): DmRow[] {
  const { channels, currentUserId, getUser, getLastMessageAt } = opts;
  const byKey = new Map<string, DmRow>();

  for (const ch of channels) {
    const otherMember = ch.members?.find((m) => m.user_id !== currentUserId);

    // No "other" member → the caller's self-DM ("Notes") or a 1:1 DM whose
    // peer's membership was tombstoned. The self-DM is surfaced separately as
    // a pinned row (see buildSelfDmRow + useDmListData's `selfDm`) so it shows
    // in every scope, not just Home; orphans are dropped. Either way it's not
    // a peer row, so skip it here.
    if (!otherMember) continue;

    const otherUserId = otherMember.user_id;
    const otherUser = getUser(otherUserId);
    if (!otherUser) continue;
    // Hide DMs whose other member has been deactivated. Their channel
    // membership row stays for history, so the DM channel still exists;
    // without this filter the sidebar shows a "Deactivated User" row that
    // opens an empty thread when clicked.
    if (otherUser.is_deactivated) continue;
    const displayName = otherUser.display_name || ch.name || "Direct message";
    const dedupeKey =
      otherUserId !== "system-bot"
        ? `user:${otherUserId}`
        : `name:${displayName.trim().toLowerCase()}`;
    const current = byKey.get(dedupeKey);
    const createdAt = ch.created_at ?? 0;
    const lastMessageAt = getLastMessageAt(ch.id);
    const activityAt = Math.max(lastMessageAt, createdAt);
    const currentActivityAt = current
      ? Math.max(current.lastMessageAt, current.createdAt)
      : -1;

    if (!current || activityAt > currentActivityAt) {
      byKey.set(dedupeKey, {
        channelId: ch.id,
        displayName,
        avatarUrl: otherUser.avatar_url,
        isOnline: Boolean(otherUser.is_online),
        lastActiveAt: otherUser.last_active_at,
        userId: otherUserId,
        lastMessageAt,
        createdAt,
        isCoworker: Boolean(otherUser.is_coworker),
        isGuest: Boolean(otherUser.is_guest),
      });
    }
  }

  return [...byKey.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );
}

/**
 * Find + build the caller's self-DM ("Notes") row from a set of DM channels,
 * or `undefined` if they have none. Surfaced separately from
 * {@link buildDirectDmRows} so the self-DM can be pinned in every scope.
 */
export function findSelfDmRow(opts: {
  channels: ReadonlyArray<DmChannelLike>;
  currentUserId: string;
  getUser: (id: string) => DmUser | undefined;
  getLastMessageAt: (channelId: string) => number;
}): DmRow | undefined {
  const { channels, currentUserId, getUser, getLastMessageAt } = opts;
  for (const ch of channels) {
    const row = buildSelfDmRow({
      channelId: ch.id,
      channelName: ch.name,
      memberUserIds: (ch.members ?? []).map((m) => m.user_id),
      currentUserId,
      me: getUser(currentUserId),
      createdAt: ch.created_at ?? 0,
      lastMessageAt: getLastMessageAt(ch.id),
    });
    if (row) return row;
  }
  return undefined;
}

/** Workspace info bundle used in the home-view shared-workspaces tooltip. */
export interface WorkspaceInfo {
  id: string;
  name: string;
  logo_url?: string | null;
  slug?: string | null;
}

/**
 * Apply the unread/recent display filter to a list of DM rows.
 *   • `unreadOnly` keeps only rows whose channel has unread messages.
 *   • `recentOnly` keeps only rows with activity (lastMessage or createdAt),
 *      sorts by activity desc, and caps at `limit`.
 *   • Otherwise returns the input unchanged.
 */
export function filterDmDisplay(
  items: DmRow[],
  unreadOnly: boolean | undefined,
  recentOnly: boolean | undefined,
  isUnread: ((id: string) => boolean) | undefined,
  lastMessageAtMap: Map<string, number | null>,
  limit = 10,
): DmRow[] {
  if (unreadOnly) return items.filter((dm) => isUnread?.(dm.channelId));
  if (recentOnly) {
    return [...items]
      .filter(
        (dm) => lastMessageAtMap.get(dm.channelId) != null || dm.createdAt > 0,
      )
      .sort(
        (a, b) =>
          (lastMessageAtMap.get(b.channelId) ?? b.createdAt) -
          (lastMessageAtMap.get(a.channelId) ?? a.createdAt),
      )
      .slice(0, limit);
  }
  return items;
}

/**
 * Same shape, but operating on group-DM channel rows (which carry an
 * `id` + optional `created_at`, not the richer DmRow shape).
 */
export function filterGroupDmDisplay<
  T extends { id: string; created_at?: number | null },
>(
  items: T[],
  unreadOnly: boolean | undefined,
  recentOnly: boolean | undefined,
  isUnread: ((id: string) => boolean) | undefined,
  lastMessageAtMap: Map<string, number | null>,
  limit = 10,
): T[] {
  if (unreadOnly) return items.filter((ch) => isUnread?.(ch.id));
  if (recentOnly) {
    return [...items]
      .filter(
        (ch) => lastMessageAtMap.get(ch.id) != null || (ch.created_at ?? 0) > 0,
      )
      .sort(
        (a, b) =>
          (lastMessageAtMap.get(b.id) ?? b.created_at ?? 0) -
          (lastMessageAtMap.get(a.id) ?? a.created_at ?? 0),
      )
      .slice(0, limit);
  }
  return items;
}
